// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Repeats the content until one loop unit fills the viewport, clones that unit once (aria-hidden, inert) and animates with WAAPI.
// Pause sources are independent flags: hover, focus-within, reduced motion, hidden tab, and a sticky explicit pause via the --toggle command.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #flags = { hover: false, focus: false, hidden: false, user: false };

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("pointerenter", () => this.#set("hover", true), { signal });
    this.addEventListener("pointerleave", () => this.#set("hover", false), { signal });
    this.addEventListener("focusin", () => this.#set("focus", true), { signal });
    this.addEventListener("focusout", (e) => { if (!this.contains(e.relatedTarget)) this.#set("focus", false); }, { signal });
    document.addEventListener("visibilitychange", () => this.#set("hidden", document.hidden), { signal });
    this.addEventListener("command", (e) => { if (e.command === "--toggle") this.#toggle(e.source); }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #set(flag, value) {
    this.#flags[flag] = value;
    this.#sync();
  }

  #toggle(source) {
    this.#flags.user = !this.#flags.user;
    if (source) {
      source.setAttribute("aria-pressed", String(this.#flags.user));
      const label = this.#flags.user ? source.dataset.labelPlay : source.dataset.labelPause;
      if (label) source.setAttribute("aria-label", label);
    }
    this.#sync();
  }

  #sync() {
    if (!this.#animation) return;
    const paused = Object.values(this.#flags).some(Boolean);
    if (paused) this.#animation.pause();
    else this.#animation.play();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    if (!track.dataset.cloned) {
      const originals = [...track.children];
      // Repeat the content until one loop unit is at least as wide as the viewport, so no gap ever shows.
      let guard = 0;
      while (track.scrollWidth < this.clientWidth && guard < 12) {
        track.append(...originals.map((n) => this.#hiddenClone(n)));
        guard++;
      }
      const unit = [...track.children];
      track.append(...unit.map((n) => this.#hiddenClone(n)));
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const rtl = getComputedStyle(this).direction === "rtl";
    const forward = this.dataset.direction !== "right";
    const sign = rtl ? 1 : -1;
    const from = forward ? 0 : sign * distance;
    const to = forward ? sign * distance : 0;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#sync();
  }

  #hiddenClone(node) {
    const clone = node.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    clone.setAttribute("inert", "");
    clone.removeAttribute("id");
    return clone;
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
